import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { errMsg } from "@/lib/api";
import useLoad from "@/lib/useLoad";
import MediaPicker from "./MediaPicker";
import { discardUpload } from "./upload";

const blank = {
  name: "", muscle_group: "", equipment: "", instructions: "", primary_muscle_id: "",
  secondary_muscle_ids: [], equipment_ids: [], category_ids: [], image_url: "",
};

const toPicker = (m) => m && { ...m, preview: m };

function MultiSelect({ label, options, value, onChange, testId }) {
  const selected = new Set(value || []);
  return (
    <div>
      <Label>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="mt-1 w-full justify-between font-normal" data-testid={testId}>
            {selected.size ? `${selected.size} selected` : `Select ${label.toLowerCase()}`}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] max-h-64 overflow-y-auto p-2 bg-[#0B1220] border-[#1E293B]">
          <div className="space-y-1">
            {(options || []).map((item) => (
              <label key={item.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-white/5 cursor-pointer">
                <Checkbox checked={selected.has(item.id)} onCheckedChange={(checked) => onChange(
                  checked ? [...new Set([...(value || []), item.id])]
                    : (value || []).filter((id) => id !== item.id)
                )} />
                <span>{item.name}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function ExerciseDialog({ open, onOpenChange, exercise, onSaved, initialName = "" }) {
  const [form, setForm] = useState(blank);
  const [media, setMedia] = useState(null);
  const [busy, setBusy] = useState(false);
  const [equipmentOptions] = useLoad("/equipment");
  const [muscleOptions] = useLoad("/muscle-types");
  const [categoryOptions] = useLoad("/exercise-categories");

  useEffect(() => {
    if (open) {
      setForm(exercise ? {
        ...blank,
        name: exercise.name,
        muscle_group: exercise.primary_muscle || exercise.muscle_group || "",
        equipment: exercise.equipment || "",
        instructions: exercise.instructions || "",
        primary_muscle_id: exercise.primary_muscle_id || "",
        secondary_muscle_ids: exercise.secondary_muscle_ids || [],
        equipment_ids: exercise.equipment_ids || [],
        category_ids: exercise.category_ids || [],
        image_url: exercise.image_url || "",
      } : { ...blank, name: initialName });
      setMedia(toPicker(exercise?.media));
    }
  }, [open, exercise, initialName]);

  const savedPath = exercise?.media?.path;
  const close = () => {
    if (media?.path && media.path !== savedPath) discardUpload(media.path);
    onOpenChange(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const legacyEquipmentId = equipmentOptions?.find((item) => item.name.toLowerCase() === form.equipment.trim().toLowerCase())?.id;
  const equipmentSelection = form.equipment_ids.length ? form.equipment_ids : legacyEquipmentId ? [legacyEquipmentId] : [];
  const primarySelection = form.primary_muscle_id || muscleOptions?.find((item) => item.name === form.muscle_group)?.id ||
    (form.muscle_group ? `legacy:${form.muscle_group}` : "none");
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const primaryId = form.primary_muscle_id || muscleOptions?.find((item) => item.name === form.muscle_group)?.id || "";
    if (!exercise && !primaryId) return toast.error("Choose a primary muscle");
    const m = media && { kind: media.kind, url: media.path ? null : media.url, path: media.path, content_type: media.content_type, aspect: media.aspect };
    setBusy(true);
    try {
      const body = { ...form, primary_muscle_id: primaryId, image_url: form.image_url.trim() || null, media: m };
      const { data } = exercise ? await api.put(`/exercises/${exercise.id}`, body) : await api.post("/exercises", body);
      toast.success(exercise ? "Exercise updated" : "Exercise added");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && close()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0B1220] border-[#1E293B]" data-testid="exercise-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">{exercise ? "Edit exercise" : "Add exercise"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input value={form.name} onChange={set("name")} placeholder="e.g. Barbell back squat" data-testid="exercise-name-input" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Primary muscle</Label>
              <Select value={primarySelection} onValueChange={(id) => setForm({
                ...form,
                primary_muscle_id: id === "none" || id.startsWith("legacy:") ? "" : id,
                muscle_group: id.startsWith("legacy:") ? id.slice(7) : muscleOptions?.find((item) => item.id === id)?.name || "",
              })}>
                <SelectTrigger className="mt-1" data-testid="exercise-muscle-input"><SelectValue placeholder="Choose primary muscle" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {form.muscle_group && !(muscleOptions || []).some((item) => item.name === form.muscle_group) && <SelectItem value={`legacy:${form.muscle_group}`}>{form.muscle_group} (legacy)</SelectItem>}
                  {(muscleOptions || []).map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <MultiSelect label="Secondary muscles" options={muscleOptions} value={form.secondary_muscle_ids} onChange={(secondary_muscle_ids) => setForm({ ...form, secondary_muscle_ids })} testId="exercise-secondary-muscles" />
            <MultiSelect label="Equipment" options={equipmentOptions} value={equipmentSelection} onChange={(equipment_ids) => setForm({ ...form, equipment_ids, equipment: "" })} testId="exercise-equipment-options" />
            <MultiSelect label="Categories" options={categoryOptions} value={form.category_ids} onChange={(category_ids) => setForm({ ...form, category_ids })} testId="exercise-category-options" />
            <div><Label>Image URL (optional)</Label><Input type="url" value={form.image_url} onChange={set("image_url")} placeholder="https://…" data-testid="exercise-image-url-input" /></div>
          </div>
          <div><Label>Notes</Label><Textarea rows={3} value={form.instructions} onChange={set("instructions")} data-testid="exercise-instructions-input" /></div>
          <MediaPicker value={media} onChange={setMedia} onBusy={setBusy} savedPath={savedPath} />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={close} disabled={busy} data-testid="exercise-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="exercise-save-btn">{busy ? "Please wait…" : "Save exercise"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
